import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import * as apicall from "../Apicall";
import UserContext from "../context/UserContext";
import { computeMetrics } from "./computeMetrics";
import OperationsTab from "./OperationsTab";
import RevenueTab from "./RevenueTab";
import LeakageTab from "./LeakageTab";
import SummaryCards from "./SummaryCards";
import ChartsSection from "./ChartsSection";
import ExportButton from "./ExportButton";
import "./analytics.css";
import FieldHelpTooltip from "../Help/FieldHelpTooltip";
import { HELP_CONTENT } from "../Help/HelpContent";
import { getBookingLabel } from "../../utils/industryLabels";

// ─── Date helpers ─────────────────────────────────────────────────────────────
// toISOString() converts to UTC first — in a timezone ahead of UTC (IST,
// Gulf, etc.), any local time before the UTC offset catches up (e.g.
// before 5:30am in IST) lands on the previous UTC day, so "today" silently
// became "yesterday" for early-morning users. Build the string from local
// Y/M/D getters instead, which never leaves the browser's own timezone.
const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getMonday = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

const getSunday = (monday) => {
  const d = new Date(monday);
  d.setDate(d.getDate() + 6);
  return d;
};

/**
 * computeDates — returns { start, end } ISO strings.
 * weekOffset:  0 = this week, -1 = last week …
 * monthOffset: 0 = this month, -1 = last month …
 */
const computeDates = (period, customRange, weekOffset = 0, monthOffset = 0) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (period) {
    case "today":
      return { start: isoDate(today), end: isoDate(today) };

    case "this_week": {
      const mon = getMonday(today);
      mon.setDate(mon.getDate() + weekOffset * 7);
      const sun = getSunday(mon);
      const end = weekOffset === 0 && sun > today ? today : sun;
      return { start: isoDate(mon), end: isoDate(end) };
    }

    case "this_month": {
      const y = today.getFullYear();
      const m = today.getMonth() + monthOffset;
      const first = new Date(y, m, 1);
      const last = new Date(y, m + 1, 0);
      const end = monthOffset === 0 && last > today ? today : last;
      return { start: isoDate(first), end: isoDate(end) };
    }

    case "yearly": {
      const first = new Date(today.getFullYear(), 0, 1);
      return { start: isoDate(first), end: isoDate(today) };
    }

    case "custom":
      return { start: customRange.start, end: customRange.end };

    default:
      return { start: null, end: null };
  }
};

const computeComparisonDates = (start, end) => {
  if (!start || !end) return { start: null, end: null };
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const duration = Math.round((e - s) / (1000 * 60 * 60 * 24));
  const prevEnd = new Date(s); prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd); prevStart.setDate(prevStart.getDate() - duration);
  return { start: isoDate(prevStart), end: isoDate(prevEnd) };
};

// ─── Human-readable period label ─────────────────────────────────────────────
export const periodLabel = (start, end, period) => {
  if (!start || !end) return "";
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const M = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  if (period === "this_month") return `${M[s.getMonth()]} ${s.getFullYear()}`;
  if (period === "yearly") return `${s.getFullYear()}`;
  if (period === "today") return `${M[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()}`;

  // week / custom — show date range
  const sameYear = s.getFullYear() === e.getFullYear();
  const sStr = `${M[s.getMonth()]} ${s.getDate()}`;
  const eStr = `${M[e.getMonth()]} ${e.getDate()}${sameYear ? "" : `, ${e.getFullYear()}`}`;
  return `${sStr} – ${eStr}`;
};

// ─── Merged metrics builder ───────────────────────────────────────────────────
function buildMergedMetrics(curr, prev) {
  const pair = (c, p) => ({ current: c, previous: p ?? null });

  const bookingsByDay = curr.bookingsByDay.map((d) => ({
    day: d.day,
    current: d.count,
    previous: prev?.bookingsByDay?.find((p) => p.day === d.day)?.count ?? 0,
  }));

  const revenuePerCrewBreakdown = curr.revenuePerCrewBreakdown.map((c) => ({
    crew_id: c.crew_id,
    crew_name: c.crew_name,
    current: c.current,
    previous: prev?.revenuePerCrewBreakdown?.find((p) => p.crew_id === c.crew_id)?.current ?? 0,
  }));

  const crewBreakdown = curr.crewBreakdown.map((c) => ({
    crew_id: c.crew_id,
    crew_name: c.crew_name,
    jobs: c.jobs,
    revenue: c.revenue,
    prevJobs: prev?.crewBreakdown?.find((p) => p.crew_id === c.crew_id)?.jobs ?? 0,
    prevRevenue: prev?.crewBreakdown?.find((p) => p.crew_id === c.crew_id)?.revenue ?? 0,
  }));

  return {
    totalBookings: pair(curr.totalBookings, prev?.totalBookings),
    totalJobsCompleted: pair(curr.totalJobsCompleted, prev?.totalJobsCompleted),
    avgJobsPerDay: pair(curr.avgJobsPerDay, prev?.avgJobsPerDay),
    jobsPerCrewDaily: pair(curr.jobsPerCrewDaily, prev?.jobsPerCrewDaily),
    jobsPerCrewWeekly: pair(curr.jobsPerCrewWeekly, prev?.jobsPerCrewWeekly),
    activeCrew: pair(curr.activeCrew, prev?.activeCrew),
    totalCrew: curr.totalCrew,
    activeCrewPct: pair(curr.activeCrewPct, prev?.activeCrewPct),
    completedVsScheduledPct: pair(curr.completedVsScheduledPct, prev?.completedVsScheduledPct),
    cancellationRatePct: pair(curr.cancellationRatePct, prev?.cancellationRatePct),
    noShowRatePct: pair(curr.noShowRatePct, prev?.noShowRatePct),
    bookingsByDay,
    // FIX for scheduling breakdown "No Data": pass both counts explicitly
    scheduledCount: pair(
      curr.scheduledCount ?? curr.totalBookings,
      prev?.scheduledCount ?? prev?.totalBookings
    ),
    peakDay: curr.peakDay,
    peakTime: curr.peakTime,
    totalRevenue: pair(curr.totalRevenue, prev?.totalRevenue),
    revenuePerBooking: pair(curr.revenuePerBooking, prev?.revenuePerBooking),
    revenuePerCrewAvg: pair(curr.revenuePerCrewAvg, prev?.revenuePerCrewAvg),
    revenuePerCrewBreakdown,
    totalCustomers: pair(curr.totalCustomers, prev?.totalCustomers),
    repeatCustomersPct: pair(curr.repeatCustomersPct, prev?.repeatCustomersPct),
    bookingsPerCustomer: pair(curr.bookingsPerCustomer, prev?.bookingsPerCustomer),
    lostRevenueNoShows: pair(curr.lostRevenueNoShows, prev?.lostRevenueNoShows),
    lostRevenueCancellations: pair(curr.lostRevenueCancellations, prev?.lostRevenueCancellations),
    noShowCount: pair(curr.noShowCount, prev?.noShowCount),
    cancelledCount: pair(curr.cancelledCount, prev?.cancelledCount),
    crewBreakdown,
  };
}

// ─── Detail tabs ──────────────────────────────────────────────────────────────

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="analytics-skeleton-card">
    <div className="analytics-skeleton-line" style={{ width: "60%", height: "12px", marginBottom: "16px" }} />
    <div className="analytics-skeleton-line" style={{ width: "50%", height: "32px", marginBottom: "8px" }} />
    <div className="analytics-skeleton-line" style={{ width: "40%", height: "10px" }} />
  </div>
);
const SkeletonGrid = () => (
  <div className="analytics-skeleton-grid">
    {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
  </div>
);

// ─── Week Picker — max 8 weeks ───────────────────────────────────────────────
const WeekPicker = ({ weekOffset, setWeekOffset }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Last 8 weeks only (offset 0 to -7)
  const options = Array.from({ length: 8 }, (_, i) => {
    const offset = -i;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const mon = getMonday(today); mon.setDate(mon.getDate() + offset * 7);
    const sun = getSunday(mon);
    const end = offset === 0 && sun > today ? today : sun;
    return {
      offset,
      label: periodLabel(isoDate(mon), isoDate(end), "this_week"),
      isThis: offset === 0,
    };
  });

  const current = options.find((o) => o.offset === weekOffset) || options[0];

  return (
    <div className="period-picker" ref={ref}>
      <button className="period-picker-btn" onClick={() => setOpen((v) => !v)}>
        {current.isThis ? "This Week" : current.label}
        <span className="period-picker-caret">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="period-picker-dropdown">
          {options.map((o) => (
            <button
              key={o.offset}
              className={`period-picker-option${o.offset === weekOffset ? " selected" : ""}`}
              onClick={() => { setWeekOffset(o.offset); setOpen(false); }}
            >
              {o.isThis
                ? <><strong>This Week</strong> <span className="period-picker-sub">{o.label}</span></>
                : o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Month Picker — max 6 months ─────────────────────────────────────────────
const MonthPicker = ({ monthOffset, setMonthOffset }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const MN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  // Last 6 months only (offset 0 to -5)
  const options = Array.from({ length: 6 }, (_, i) => {
    const offset = -i;
    const today = new Date();
    const d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    return { offset, label: `${MN[d.getMonth()]} ${d.getFullYear()}`, isThis: offset === 0 };
  });

  const current = options.find((o) => o.offset === monthOffset) || options[0];

  return (
    <div className="period-picker" ref={ref}>
      <button className="period-picker-btn" onClick={() => setOpen((v) => !v)}>
        {current.isThis ? "This Month" : current.label}
        <span className="period-picker-caret">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="period-picker-dropdown">
          {options.map((o) => (
            <button
              key={o.offset}
              className={`period-picker-option${o.offset === monthOffset ? " selected" : ""}`}
              onClick={() => { setMonthOffset(o.offset); setOpen(false); }}
            >
              {o.isThis
                ? <><strong>This Month</strong> <span className="period-picker-sub">{o.label}</span></>
                : o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const AnalyticsDashboard = () => {
  const { vendor, user } = useContext(UserContext);

  // Currency: from vendor context first, then localStorage, then detect from location
  const currency = vendor?.currency
    || localStorage.getItem("currency")
    || (navigator.language === "en-IN" ? "INR"
      : navigator.language?.startsWith("ar") ? "AED"
        : "USD");

  const userName = vendor?.business_name || user?.name || "Overview of your business";
  const userAvatar = (vendor?.business_name || user?.name || "B").charAt(0).toUpperCase();

  // Today's ISO date — used to cap the custom date picker max value
  const todayIso = isoDate(new Date());

  const [period, setPeriod] = useState("this_week");
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [customRange, setCustomRange] = useState({ start: null, end: null });
  const [activeTab, setActiveTab] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [industry, setIndustry] = useState(null);
  const isClinicService = industry?.industry === 1;
  const isSportsService = industry?.industry === 7;
  const isCarService = industry?.industry === 8;
  const isSalonService = industry?.industry === 9;

  const crewSingular = isClinicService ? "Doctor" : isSportsService ? "Coach" : (isCarService || isSalonService) ? "Service Provider" : "Crew";
  const crewPlural = isClinicService ? "Doctors" : isSportsService ? "Coaches" : (isCarService || isSalonService) ? "Service Providers" : "Crew";
  const bookingPluralLabel = getBookingLabel(industry, true);

const DETAIL_TABS = [
  isClinicService
    ? "Operations & Doctors"
    : isSportsService
    ? "Operations & Sports"
    : (isCarService || isSalonService)
    ? "Operations & Service Providers"
    : "Operations & Crew",
  "Revenue & Growth",
  "Loss & Leakage",
];
const providerHelp = {
  periodToday: isClinicService
    ? HELP_CONTENT.analytics.DoctorperiodToday
    : HELP_CONTENT.analytics.periodToday,

}
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await apicall.get("ezpMyProfile");
        if (res?.success && res.data?.[0]) {
          setIndustry(res.data[0]);
        }
      } catch (err) {
        console.error("Error fetching vendor profile:", err);
      }
    };
    fetchProfile();
  }, []);
console.log(metrics,"metrics")
  const dateRange = computeDates(period, customRange, weekOffset, monthOffset);
  const prevDates = computeComparisonDates(dateRange.start, dateRange.end);
  const thisPeriodLabel = periodLabel(dateRange.start, dateRange.end, period);
  const prevPeriodLabel = periodLabel(prevDates.start, prevDates.end, period);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { start, end } = computeDates(period, customRange, weekOffset, monthOffset);
      const prev = computeComparisonDates(start, end);

      const [currRes, prevRes] = await Promise.all([
        apicall.post({ start_date: start || null, end_date: end || null }, "analytics"),
        prev.start
          ? apicall.post({ start_date: prev.start, end_date: prev.end }, "analytics")
          : Promise.resolve({ data: [] }),
      ]);
      
      const currRows = Array.isArray(currRes.data) ? currRes.data : [];
      const prevRows = Array.isArray(prevRes.data) ? prevRes.data : [];
      const currMetrics = computeMetrics(currRows);
      const prevMetrics = prevRows.length ? computeMetrics(prevRows) : null;
      setMetrics(currMetrics ? buildMergedMetrics(currMetrics, prevMetrics) : null);
    } catch (err) {
      setError(err?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [period, customRange, weekOffset, monthOffset]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePeriodChange = (p) => {
    setPeriod(p);
    setWeekOffset(0);
    setMonthOffset(0);
    // When switching to custom, pre-fill with today so calendar opens on current date
    if (p === "custom") {
      setCustomRange({ start: todayIso, end: todayIso });
    }
  };

  // Common props passed to all tabs
  const tabProps = { metrics, currency, thisPeriodLabel, prevPeriodLabel, isClinicService, isSportsService, crewSingular, crewPlural, bookingPluralLabel };

  return (
    <div className="analytics-root">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="analytics-header">
        <div className="analytics-header-left">
          <div>
            <h1 className="analytics-title">Business Dashboard</h1>
            <p className="analytics-subtitle">{userName} · {currency}</p>
          </div>
        </div>
        <div className="analytics-header-actions">
          <button className="analytics-refresh-btn" onClick={fetchData}>↻ Refresh</button>
          <span data-tour="analytics-export">
            <ExportButton metrics={metrics} currency={currency} period={period} dateRange={dateRange} isClinicService={isClinicService} isSportsService={isSportsService} crewSingular={crewSingular} crewPlural={crewPlural} />
          </span>
        </div>
      </div>

      {/* ── Period Bar ──────────────────────────────────────────────── */}
      <div className="analytics-period-bar" data-tour="analytics-period">

        {/* Today — clicking opens a date picker calendar */}
        <div className="period-btn-group" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <FieldHelpTooltip {...providerHelp.periodToday} placement="bottom" />
          <button
            className={`analytics-period-btn${period === "today" ? " active" : ""}`}
            onClick={() => {
              handlePeriodChange("today");
              // Open the hidden date input
              document.getElementById("today-date-input")?.showPicker?.();
            }}
          >
            📅 {period === "today" && dateRange.start
              ? (() => {
                const d = new Date(dateRange.start + "T00:00:00");
                return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
              })()
              : "Today"}
          </button>
          {/* Hidden date input — triggers native calendar */}
          <input
            id="today-date-input"
            type="date"
            max={todayIso}
            value={period === "today" ? (dateRange.start || todayIso) : todayIso}
            onChange={(e) => {
              if (!e.target.value) return;
              setPeriod("today");
              setWeekOffset(0);
              setMonthOffset(0);
              setCustomRange({ start: e.target.value, end: e.target.value });
              // Override computeDates for "today" to use selected date
              // We'll store it in customRange and switch period to "custom"
              setPeriod("custom");
              setCustomRange({ start: e.target.value, end: e.target.value });
            }}
            style={{
              position: "absolute",
              opacity: 0,
              pointerEvents: "none",
              width: 0,
              height: 0,
            }}
          />
        </div>

        {/* This Week with inline picker */}
        <div className="period-btn-group" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <FieldHelpTooltip {...HELP_CONTENT.analytics.periodWeek} placement="bottom" />
          <button
            className={`analytics-period-btn${period === "this_week" ? " active" : ""}`}
            onClick={() => handlePeriodChange("this_week")}
          >
            This Week
          </button>
          {period === "this_week" && (
            <WeekPicker weekOffset={weekOffset} setWeekOffset={setWeekOffset} />
          )}
        </div>

        {/* This Month with inline picker */}
        <div className="period-btn-group" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <FieldHelpTooltip {...HELP_CONTENT.analytics.periodMonth} placement="bottom" />
          <button
            className={`analytics-period-btn${period === "this_month" ? " active" : ""}`}
            onClick={() => handlePeriodChange("this_month")}
          >
            This Month
          </button>
          {period === "this_month" && (
            <MonthPicker monthOffset={monthOffset} setMonthOffset={setMonthOffset} />
          )}
        </div>

        <button
          className={`analytics-period-btn${period === "yearly" ? " active" : ""}`}
          onClick={() => handlePeriodChange("yearly")}
        >
          This Year
        </button>

        <button
          className={`analytics-period-btn${period === "custom" ? " active" : ""}`}
          onClick={() => handlePeriodChange("custom")}
        >
          Custom Range
        </button>

        {/* Inline label next to Custom when a range is selected */}
        {period === "custom" && dateRange.start && dateRange.end && (
          <span className="analytics-custom-active-label">
            📅 {periodLabel(dateRange.start, dateRange.end, "custom")}
          </span>
        )}
      </div>

      {/* Custom date pickers */}
      {period === "custom" && (
        <div className="analytics-custom-range">
          <label className="analytics-custom-label">From</label>
          <input
            type="date"
            className="analytics-date-input"
            max={todayIso}
            value={customRange.start || todayIso}
            onChange={(e) => setCustomRange((r) => ({ ...r, start: e.target.value }))}
          />
          <label className="analytics-custom-label">To</label>
          <input
            type="date"
            className="analytics-date-input"
            max={todayIso}
            value={customRange.end || todayIso}
            onChange={(e) => setCustomRange((r) => ({ ...r, end: e.target.value }))}
          />
        </div>
      )}

      {/* ── Period context chips ─────────────────────────────────────── */}
      {!loading && (thisPeriodLabel || prevPeriodLabel) && (
        <div className="analytics-period-context">
          {thisPeriodLabel && (
            <span className="period-context-chip period-context-current">
              This Period: <strong>{thisPeriodLabel}</strong>
            </span>
          )}
          {prevPeriodLabel && (
            <span className="period-context-chip period-context-prev">
              vs Previous: <strong>{prevPeriodLabel}</strong>
            </span>
          )}
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────── */}
      {error && (
        <div className="analytics-error-banner">
          <span>⚠ {error}</span>
          <button onClick={fetchData}>Retry</button>
        </div>
      )}

      {/* ── Content ─────────────────────────────────────────────────── */}
      {loading ? (
        <SkeletonGrid />
      ) : !metrics ? (
        <div className="analytics-empty">
          <div className="analytics-empty-icon">📊</div>
          <div className="analytics-empty-title">No data for this period</div>
          <div className="analytics-empty-sub">Try a different date range.</div>
        </div>
      ) : (
        <>
          <div data-tour="analytics-kpis">
            <SummaryCards {...tabProps} />
          </div>
          <ChartsSection {...tabProps} />

          <div className="analytics-tabs-bar" data-tour="analytics-tabs">
            {DETAIL_TABS.map((tab, i) => (
              <button
                key={tab}
                className={`analytics-tab-btn${activeTab === i ? " active" : ""}`}
                onClick={() => setActiveTab(i)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="analytics-tab-content">
            {activeTab === 0 && <OperationsTab {...tabProps} />}
            {activeTab === 1 && <RevenueTab    {...tabProps} />}
            {activeTab === 2 && <LeakageTab    {...tabProps} />}
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboard;